import memoize from 'lodash-es/memoize';

export interface FormatNumberOptions {
  language: string;
  exp?: number | null;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
  compactNotation?: boolean;
}

// @translate
type NumberName = 'trln' | 'bln' | 'mln';

const exponentPowers = [1e12, 1e9, 1e6];
const exponentNames: {
  [a: number]: NumberName;
} = { 1e12: 'trln', 1e9: 'bln', 1e6: 'mln' };

function formatInteger(value: number, exp?: number | null): [number, NumberName | undefined] {
  const newValue = value * Math.pow(10, exp || 0);

  for (let index = 0; index < exponentPowers.length; index++) {
    const element = exponentPowers[index];

    if (newValue >= element) {
      return [newValue / element, exponentNames[element]];
    }
  }

  return [newValue, undefined];
}
const getDelimiter = memoize((language: string) => {
  const intl = new Intl.NumberFormat(language, { minimumFractionDigits: 1 });
  return intl.format(1.1).slice(1, 2);
});

export function formatNumber(
  value: number,
  {
    language,
    maximumFractionDigits,
    minimumFractionDigits,
    exp,
    compactNotation,
  }: FormatNumberOptions
) {
  const intl = new Intl.NumberFormat(language, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  const absValue = Math.abs(value);
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const delim = getDelimiter(language);

  const [bigValue, name] = compactNotation ? formatInteger(absValue, exp) : [absValue, undefined];

  const [integer, fraction] = intl.format(bigValue || absValue).split(delim);

  return {
    integer,
    fraction,
    name,
    delim,
    sign,
  };
}
